import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';

const CHATBOT_SCRIPT_ID = 'leadconnector-ai-chatbot-loader';
const CHATBOT_VISIBLE_CLASS = 'og-ai-chatbot-visible';
const CHATBOT_SCRIPT_URL = 'https://widgets.leadconnectorhq.com/loader.js';
const CHATBOT_RESOURCES_URL = 'https://widgets.leadconnectorhq.com/chat-widget/loader.js';
const CHATBOT_WIDGET_ID = '6a6112ca7742c156ec746465';

interface LeadConnectorWindow extends Window {
  leadConnector?: {
    chatWidget?: {
      closeWidget?: () => void;
    };
  };
}

@Injectable({ providedIn: 'root' })
export class AiChatbotService {
  private readonly renderer: Renderer2;
  private isMounted = false;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    rendererFactory: RendererFactory2
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  mount(): void {
    if (this.isMounted) {
      return;
    }

    this.isMounted = true;
    this.renderer.addClass(this.document.body, CHATBOT_VISIBLE_CLASS);
    this.loadScriptOnce();
  }

  unmount(): void {
    this.isMounted = false;

    try {
      const chatWidget = (this.document.defaultView as LeadConnectorWindow | null)?.leadConnector
        ?.chatWidget;
      chatWidget?.closeWidget?.();
    } catch {
      // Third-party widget failures must never interrupt Angular navigation.
    } finally {
      // The visibility boundary must be removed even if third-party widget code fails.
      this.renderer.removeClass(this.document.body, CHATBOT_VISIBLE_CLASS);
    }
  }

  private loadScriptOnce(): void {
    if (this.document.getElementById(CHATBOT_SCRIPT_ID)) {
      return;
    }

    const script = this.renderer.createElement('script') as HTMLScriptElement;
    this.renderer.setAttribute(script, 'id', CHATBOT_SCRIPT_ID);
    this.renderer.setAttribute(script, 'src', CHATBOT_SCRIPT_URL);
    this.renderer.setAttribute(script, 'data-resources-url', CHATBOT_RESOURCES_URL);
    this.renderer.setAttribute(script, 'data-widget-id', CHATBOT_WIDGET_ID);
    this.renderer.setProperty(script, 'async', true);

    const removeErrorListener = this.renderer.listen(script, 'error', () => {
      removeErrorListener();
      script.remove();
    });

    this.renderer.appendChild(this.document.body, script);
  }
}
